import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Star, User, Calendar } from 'lucide-react';

const ReviewsList = ({ reviews = [], showTitle = true }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'text-amber-400 fill-amber-400'
                : 'text-slate-200'
            }`}
          />
        ))}
      </div>
    );
  };

  if (reviews.length === 0) {
    return (
      <div data-testid="reviews-list-empty">
        {showTitle && (
          <h3 
            className="text-lg font-semibold text-[#1A237E] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Avis des patients (0)
          </h3>
        )}
        <div className="text-center py-8">
          <Star className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500">Aucun avis pour le moment</p>
        </div>
      </div>
    );
  }

  // Calculate stats
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;
  
  const ratingCounts = [5, 4, 3, 2, 1].map(rating => ({
    rating,
    count: reviews.filter(r => r.rating === rating).length,
    percentage: (reviews.filter(r => r.rating === rating).length / reviews.length) * 100,
  }));

  return (
    <div>
      {showTitle && (
        <h3 
          className="text-lg font-semibold text-[#1A237E] mb-4"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Avis des patients ({reviews.length})
        </h3>
      )}

      {/* Rating Summary */}
      <Card className="mb-6 border-0 bg-[#F8FAFC]">
        <CardContent className="p-5">
          <div className="flex items-start gap-6">
            {/* Average Score */}
            <div className="text-center">
              <div className="text-4xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {avgRating}
              </div>
              <div className="flex justify-center my-1">
                {renderStars(Math.round(parseFloat(avgRating)))}
              </div>
              <div className="text-sm text-slate-500">
                {reviews.length} avis
              </div>
            </div>

            {/* Rating Breakdown */}
            <div className="flex-1 space-y-2">
              {ratingCounts.map(({ rating, count, percentage }) => (
                <div key={rating} className="flex items-center gap-2">
                  <span className="text-sm text-slate-600 w-3">{rating}</span>
                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-400 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-500 w-8">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.map((review) => (
          <Card key={review.id} className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E53935] to-[#D32F2F] flex items-center justify-center text-white font-semibold">
                    {review.patient_name?.charAt(0) || 'P'}
                  </div>
                  <div>
                    <p className="font-medium text-[#1A237E]">{review.patient_name}</p>
                    <div className="flex items-center gap-2">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Calendar className="w-3 h-3" />
                  {formatDate(review.created_at)}
                </div>
              </div>
              
              {review.comment && (
                <p className="text-slate-600 text-sm leading-relaxed pl-13">
                  {review.comment}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ReviewsList;
